import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, CalendarDays } from "lucide-react";
import { Edicao } from "@/types/edicao";
import { listarEdicoes, deletarEdicao } from "@/lib/edicoes";
import EdicaoModal from "@/components/admin/edicoes/EdicaoModal";

export default function AdminEdicoes() {
  const navigate = useNavigate();
  const [edicoes, setEdicoes] = useState<Edicao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [edicaoSelecionada, setEdicaoSelecionada] = useState<Edicao | null>(null);
  const [deletando, setDeletando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const dados = await listarEdicoes();
      setEdicoes(dados);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNovaEdicao = () => {
    setEdicaoSelecionada(null);
    setModalAberto(true);
  };

  const abrirEditar = (edicao: Edicao) => {
    setEdicaoSelecionada(edicao);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setEdicaoSelecionada(null);
  };

  const aoSalvar = () => {
    fecharModal();
    carregar();
  };

  const confirmarDelete = async (edicao: Edicao) => {
    const ok = window.confirm(
      `Deseja realmente excluir a edição "${edicao.nomeLocal} — ${edicao.cidade}/${edicao.uf}"?`
    );
    if (!ok) return;
    setDeletando(edicao.id);
    try {
      await deletarEdicao(edicao.id);
      await carregar();
    } finally {
      setDeletando(null);
    }
  };

  return (
    <>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-black">Edições</h1>
            <p className="text-sm text-black/40">
              {edicoes.length > 0
                ? `${edicoes.length} edição${edicoes.length > 1 ? "s" : ""} cadastrada${edicoes.length > 1 ? "s" : ""}`
                : "Gerencie as edições do evento."}
            </p>
          </div>
          <button
            onClick={abrirNovaEdicao}
            className="flex items-center gap-2 px-4 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition w-full sm:w-auto justify-center"
          >
            <Plus className="w-4 h-4" />
            Nova Edição
          </button>
        </div>

        {/* Conteúdo */}
        {carregando ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-black/20" />
          </div>
        ) : edicoes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/5 p-16 flex flex-col items-center justify-center text-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-black/5 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-black/20" />
            </div>
            <p className="text-sm font-medium text-black/50">
              Nenhuma edição cadastrada ainda.
            </p>
            <p className="text-xs text-black/30">
              Clique em &ldquo;Nova Edição&rdquo; para começar.
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {edicoes.map((edicao) => (
              <div
                key={edicao.id}
                className="bg-white rounded-2xl border border-black/5 p-5 flex gap-4 items-center hover:border-black/10 hover:shadow-sm transition group cursor-pointer"
                onClick={() => navigate(`/admin/edicoes/${edicao.id}`)}
              >
                {/* Data destaque */}
                <div className="shrink-0 w-14 h-14 bg-black rounded-xl flex flex-col items-center justify-center text-white">
                  <span className="text-xs font-medium opacity-60 uppercase tracking-wider leading-none">
                    {edicao.data
                      ? new Date(edicao.data + "T12:00:00").toLocaleString("pt-BR", { month: "short" })
                      : "—"}
                  </span>
                  <span className="text-xl font-bold leading-tight">
                    {edicao.data
                      ? new Date(edicao.data + "T12:00:00").getDate()
                      : "—"}
                  </span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-black truncate">
                    {edicao.nomeLocal}
                  </h3>
                  <span className="text-xs text-black/50">
                    {edicao.cidade}/{edicao.uf}
                  </span>
                </div>

                {/* Ações */}
                <div className="flex items-center gap-1 shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition">
                  <button
                    onClick={(e) => { e.stopPropagation(); abrirEditar(edicao); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-black hover:bg-black/5 transition"
                    title="Editar"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); confirmarDelete(edicao); }}
                    disabled={deletando === edicao.id}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-50"
                    title="Excluir"
                  >
                    {deletando === edicao.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalAberto && (
        <EdicaoModal
          edicao={edicaoSelecionada}
          onClose={fecharModal}
          onSaved={aoSalvar}
        />
      )}
    </>
  );
}
